/* 0.49.61 - The public site's first requests, started at once.
 *
 * Before, a first load (a visit, a refresh, a link from outside) waited
 * for the IP access check, then asked for about ten things one by one
 * (languages, translations, settings, layout, menu, who is signed in,
 * flags, button templates, the handover targets, Months and days), and
 * only then for the page itself: three rounds, with the browser's six
 * connections queuing.
 *
 * Now, as soon as the script runs:
 *   - one request, /api/public/bootstrap, answers those ten together
 *     (the server runs each of them exactly as the browser would have,
 *     with the same cookies and address);
 *   - the page (or the home page, or the flow) and the access check are
 *     asked for at the same moment, beside it.
 * When the site's own code asks for one of these addresses a moment
 * later, it gets that answer instead of a new request.
 *
 * Nothing is kept: each answer is used once, only in the first 15
 * seconds of the visit, and only for the very address it was made for.
 * Anything else, a second request for the same address, or an answer
 * that did not come, goes to the server as before. Off on the admin
 * address. */
import { BASE, appPath } from './basePath';
import { isAdminHost } from '../utils/adminHost';

const MAX_AGE_MS = 15000;
const startedAt = Date.now();
const waiting = new Map();   // address -> Promise<Response | null>

function keyOf(input) {
  let u = typeof input === 'string' ? input : (input && input.url) || '';
  try {
    const url = new URL(u, window.location.href);
    if (url.origin !== window.location.origin) return '';
    u = url.pathname + url.search;
  } catch (_e) { return ''; }
  if (BASE && (u === BASE || u.startsWith(`${BASE}/`))) u = u.slice(BASE.length) || '/';
  return u;
}

function langOf(path) {
  const seg = path.split('/')[1] || '';
  if (/^[a-z]{2,3}(-[A-Za-z]{2,4})?$/.test(seg) && seg !== 'p') return seg;
  try { return localStorage.getItem('caf_lang') || 'hy'; } catch (_e) { return 'hy'; }
}

function start() {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return;
  const path = appPath(window.location.pathname) || '/';
  if (isAdminHost() || path === '/admin' || path.startsWith('/admin/') || path.startsWith('/login')) return;
  const lang = langOf(path);
  const realFetch = window.fetch;
  const opts = { credentials: 'include', cache: 'no-store' };

  // The ten start requests, together.
  const batch = realFetch(`/api/public/bootstrap?lang=${encodeURIComponent(lang)}`, opts)
    .then(r => (r.ok ? r.json() : null))
    .then(d => (d && d.items && typeof d.items === 'object' ? d.items : {}))
    .catch(() => ({}));
  const fromBatch = (addr) => batch.then((items) => {
    const it = items[addr];
    if (!it || typeof it.body !== 'string') return null;
    return new Response(it.status === 204 ? null : it.body,
      { status: it.status, headers: it.type ? { 'Content-Type': it.type } : {} });
  });
  for (const addr of [
    '/api/i18n/languages', '/api/public/settings', '/api/layout', '/api/auth/me',
    '/api/iso/country-flags', '/api/public/handover/targets', '/api/button-templates',
    `/api/i18n/translations/${lang}`, `/api/nav-menu?lang=${encodeURIComponent(lang)}`,
    `/api/form-libraries/months_and_days/rows?lang=${encodeURIComponent(lang)}`,
  ]) waiting.set(addr, fromBatch(addr));

  // Beside it: the access check and what this address shows.
  const own = (addr) => waiting.set(addr, realFetch(addr, opts).catch(() => null));
  own('/api/public/access?scope=public');
  const q = `?lang=${encodeURIComponent(lang)}`;
  let m;
  if (path === '/' || path === '') own(`/api/pages/landing-page${q}`);
  else if ((m = /^\/[^/]+\/p\/([A-Za-z0-9_-]+)\/?$/.exec(path))) own(`/api/pages/${m[1]}${q}`);
  else if ((m = /^\/[^/]+\/services\/([A-Za-z0-9_-]+)(?:\/[^/]*)?\/?$/.exec(path))) own(`/api/flows/${m[1]}`);

  window.fetch = (input, init) => {
    const method = String((init && init.method) || (input && typeof input !== 'string' && input.method) || 'GET').toUpperCase();
    if (method === 'GET' && waiting.size && Date.now() - startedAt < MAX_AGE_MS) {
      const addr = keyOf(input);
      const ready = addr && waiting.get(addr);
      if (ready) {
        waiting.delete(addr);   // once
        return ready.then(r => r || realFetch(input, init));
      }
    }
    return realFetch(input, init);
  };
}

start();
